/**
 * OAuth-Callback-Hash-Parser (Phase 3.5 §3).
 *
 * Worker-Callback redirected zu `${frontend_origin}/login/callback` mit
 * Bearer-Token im URL-Hash:
 *
 *     /login/callback#token=eyJ...&exp=1716579600&email=...&redirect_to=/hub
 *
 * Hash wird NICHT zum Server gesendet (Browser-Spec) — bleibt im Client.
 * Wir parsen, säubern die URL (token raus aus Browser-History), und geben
 * dem Caller die Daten zum Persist + Navigate.
 *
 * Sicherheitsannahme: wenn der Hash existiert, ist er vom Worker. Eine
 * Verifikation der Signatur passiert client-side NICHT (das macht jeder
 * folgende API-Call via Bearer-Header automatisch). Falls jemand einen
 * Token-Hash injiziert: nächster API-Call schlägt mit 401 fehl → Auto-Logout.
 */

export const CALLBACK_PATH = "/login/callback";

export type ParsedCallback = {
  token: string;
  exp: number;
  email: string;
  redirectTo: string;
};

/**
 * Parsed den Hash an `/login/callback`. Returnt null wenn nicht auf der
 * Callback-Route oder Hash unvollständig.
 *
 * **Seiteneffekt:** entfernt den Hash aus der Browser-URL via
 * `history.replaceState` — Token landet nicht im Browser-History/Back-Button.
 */
export function handleOAuthCallback(): ParsedCallback | null {
  if (window.location.pathname !== CALLBACK_PATH) return null;

  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  const params = new URLSearchParams(hash.slice(1)); // ohne führendes "#"
  const token = params.get("token") ?? "";
  const expRaw = params.get("exp") ?? "";
  const email = params.get("email") ?? "";
  const redirectToRaw = params.get("redirect_to") ?? "";

  const exp = Number.parseInt(expRaw, 10);
  if (!token || !Number.isFinite(exp) || exp <= 0) {
    return null;
  }

  // Hash + pathname säubern damit Token nicht in Browser-History bleibt.
  // Replace statt push damit Back-Button die Callback-URL nicht zeigt.
  const target = sanitizeRedirectTarget(redirectToRaw);
  window.history.replaceState(null, "", target);

  return { token, exp, email, redirectTo: target };
}

/**
 * Whitelist gegen Open-Redirect: nur Pfade die mit `/` beginnen und nicht
 * `//` (protocol-relative). Sonst → `/` als Fallback.
 *
 * Erfüllt OWASP A1:2021 (Broken Access Control) für Open-Redirect-Schutz.
 */
export function sanitizeRedirectTarget(raw: string): string {
  if (!raw) return "/";
  // Strip whitespace
  const trimmed = raw.trim();
  if (!trimmed.startsWith("/")) return "/";
  if (trimmed.startsWith("//")) return "/"; // protocol-relative
  if (trimmed.startsWith("/\\")) return "/"; // exotic browser-quirk
  // Pfad-only-Whitelist: kein Doppelpunkt (kein `javascript:` etc.)
  if (trimmed.includes(":")) return "/";
  return trimmed;
}
